import { BroadcastChannelManager } from './BroadcastChannelManager';
import type { IBroadcastChannelManagerOptions } from './BroadcastChannelManager.types';

interface IResettable {
  reset: () => void;
}

const isResettable = (value: unknown): value is IResettable =>
  typeof (value as IResettable | null)?.reset === 'function';

/**
 * Singleton registry that lazily creates and caches broadcast channel
 * instances by name. Uses a generic factory-based approach so any
 * BroadcastChannelManager subclass can be registered without the
 * registry depending on it directly.
 *
 * @example
 * const settings = broadcastRegistry.getChannel<ISettings>('user-settings', {
 *   storageKey: 'user-settings',
 *   initialValue: { theme: 'dark' },
 *   serialize: (v) => JSON.stringify(v),
 *   deserialize: (raw) => JSON.parse(raw),
 * });
 * settings.setValue({ theme: 'light' });
 */
export class BroadcastManagerRegistry {
  private channels = new Map<string, BroadcastChannelManager<unknown>>();

  getOrCreate<T>(
    channelName: string,
    factory: () => BroadcastChannelManager<T>,
  ): BroadcastChannelManager<T> {
    const existing = this.channels.get(channelName);
    if (existing) {
      return existing as BroadcastChannelManager<T>;
    }
    const manager = factory();
    this.channels.set(channelName, manager as BroadcastChannelManager<unknown>);
    return manager;
  }

  getChannel<T>(
    channelName: string,
    options: Omit<IBroadcastChannelManagerOptions<T>, 'channelName'>,
  ): BroadcastChannelManager<T> {
    return this.getOrCreate(channelName, () => new BroadcastChannelManager<T>({ channelName, ...options }));
  }

  resetCounter(channelName: string): void {
    const existing = this.channels.get(channelName);
    if (isResettable(existing)) {
      existing.reset();
    }
  }

  destroy(channelName: string): void {
    const manager = this.channels.get(channelName);
    if (manager) {
      manager.destroy();
      this.channels.delete(channelName);
    }
  }

  destroyAll(): void {
    this.channels.forEach((m) => m.destroy());
    this.channels.clear();
  }
}

export const broadcastRegistry = new BroadcastManagerRegistry();
